import clsx from 'clsx';
import type { EmitterSource } from 'quill';
import Quill from 'quill';
import type { Delta } from 'quill/core';
import { useCallback, useEffect, useId, useRef } from 'react';
import 'quill/dist/quill.snow.css';
import { debounce, isEqual } from 'lodash';
import type { InternalModule } from 'quill-table-up';
import TableUp, {
  defaultCustomSelect,
  TableAlign,
  TableMenuContextmenu,
  TableResizeLine,
  TableResizeScale,
  TableSelection,
  TableVirtualScrollbar,
  tableMenuTools,
} from 'quill-table-up';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonDataItem } from '../../generic-types';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import 'quill-table-up/index.css';
import 'quill-table-up/table-creator.css';

Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);

// The rich text editor's semantic HTML empty value
export const EMPTY_VALUE = '<p></p>';

type TableUpInstance = TableUp & {
  modules: Record<string, InternalModule>;
};

export interface RichTextAreaProps extends DynamicFieldComponentProps<string> {
  height?: number;
  isEditor?: boolean;
  toolbar?: Array<Array<string | JsonDataItem>>;
}

// How To configure: https://quilljs.com/docs/modules/toolbar/
const defaultToolbar = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['image', 'link'],
  ['clean'],
  [{ [TableUp.toolName]: [] }],
];

const RichTextArea = ({
  label,
  error,
  value,
  height = 108,
  isEditor = true,
  onChange,
  toolbar = defaultToolbar,
  readOnly,
  required,
  inputRef,
  labelStyles,
}: RichTextAreaProps) => {
  const editorRef = useRef<Quill | undefined>(undefined);

  const containerRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const guid = useId();
  const imageInputRef = useRef<HTMLInputElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect: initializes Quill editor once, destroyEditor is stable
  useEffect(() => {
    if (editorContainerRef.current && !editorRef.current) {
      // instantiate the quill editor
      const quill = new Quill(editorContainerRef.current, {
        theme: 'snow',
        readOnly: isEditor === true ? readOnly : true,
        modules: {
          toolbar:
            isEditor === true
              ? {
                  container: toolbar,
                  handlers: {
                    link: toolbarLinkHandler,
                    image: toolbarImageHandler,
                  },
                }
              : false,
          [TableUp.moduleName]: {
            customSelect: defaultCustomSelect,
            customBtn: true,
            texts: {
              InsertCaption: 'Insert Table Title',
              CopyCell: 'Copy Cell',
              CutCell: 'Cut Cell',
              InsertTop: 'Insert Row Above',
              InsertRight: 'Insert Column Right',
              InsertBottom: 'Insert Row Below',
              InsertLeft: 'Insert Column Left',
              DeleteTable: 'Delete Table',
              SwitchWidth: 'Switch Table Width',
              fullCheckboxText: 'Insert Full Width Table',
            },
            modules: [
              { module: TableVirtualScrollbar },
              { module: TableAlign },
              { module: TableResizeLine },
              { module: TableResizeScale },
              { module: TableSelection },
              {
                module: TableMenuContextmenu,
                options: {
                  tipText: true,
                  tools: [
                    tableMenuTools.InsertCaption,
                    tableMenuTools.InsertTop,
                    tableMenuTools.InsertRight,
                    tableMenuTools.InsertBottom,
                    tableMenuTools.InsertLeft,
                    tableMenuTools.Break,
                    tableMenuTools.MergeCell,
                    tableMenuTools.SplitCell,
                    tableMenuTools.Break,
                    tableMenuTools.DeleteRow,
                    tableMenuTools.DeleteColumn,
                    tableMenuTools.DeleteTable,
                    tableMenuTools.Break,
                    tableMenuTools.CopyCell,
                    tableMenuTools.CutCell,
                    tableMenuTools.Break,
                    tableMenuTools.SwitchWidth,
                  ],
                },
              },
            ],
          },
        },
      });

      // configure the base styles
      quill.root.setAttribute('style', 'overflow: auto; min-height: 0px; max-height: none');

      // Disables copy interception - Fixes MYOSH-7818
      quill.clipboard.onCaptureCopy = () => {};

      // set initial value
      if (value) {
        quill.setContents(quill.clipboard.convert({ html: value }), 'silent');
      }

      // attach text change listener
      quill.on('text-change', onValueChange);

      // save editor ref
      editorRef.current = quill;
      inputRef?.(quill);
    }

    return () => destroyEditor();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: sync editor content when value changes, isEditor is checked conditionally
  useEffect(() => {
    if (isEditor === true && editorRef.current && !editorRef.current.hasFocus()) {
      if (!value && editorRef.current.getSemanticHTML() !== EMPTY_VALUE) {
        editorRef.current.setText('');
      } else if (value && !isEqual(editorRef.current.getSemanticHTML(), value)) {
        editorRef.current.setContents(editorRef.current.clipboard.convert({ html: value }), 'silent');
      }
    }
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: update editor config when readOnly changes, height and isEditor are checked conditionally
  useEffect(() => {
    const tableUpModule = editorRef.current?.getModule(TableUp.moduleName) as TableUpInstance;
    if (isEditor === true && editorRef.current) {
      const minHeight = readOnly === true ? '0px' : `${height}px`;
      editorRef.current.root.style.setProperty('min-height', minHeight);
      editorRef.current.root.setAttribute('spellcheck', 'true');
      if (readOnly !== undefined) {
        editorRef.current.enable(!readOnly);
      }
    }

    // Removes or adds table cell actions when toggling read/edit mode. Also removes table actions on history comments
    if (tableUpModule) {
      if (!readOnly && isEditor === true) {
        tableUpModule.initModules();
      } else if (readOnly || isEditor === false) {
        const modules = Object.values(tableUpModule.modules);
        for (let i = 0; i < modules.length; i++) {
          modules[i].destroy();
        }
      }
    }
  }, [readOnly]);

  const destroyEditor = useCallback(() => {
    if (containerRef.current) {
      const toolbar = containerRef.current.querySelector('.ql-toolbar');
      if (toolbar) {
        containerRef.current.removeChild(toolbar);
      }
    }

    if (editorContainerRef.current) {
      editorContainerRef.current.innerHTML = '';
    }

    if (editorRef.current) {
      editorRef.current.off('text-change');
      editorRef.current = undefined;
    }
  }, []);

  const toolbarLinkHandler = useCallback(() => {
    const currentEditor = editorRef.current;
    if (currentEditor) {
      //User's cursor is in the editor
      const range = currentEditor.getSelection();
      if (range) {
        const linkValue = prompt('Enter a link (URL)');
        if (linkValue) {
          //If no selection of a text in the editor,
          // then paste the linkValue first
          const insertLinkText = range.length === 0;
          if (insertLinkText) {
            currentEditor.insertText(range.index, linkValue);
            currentEditor.setSelection(range.index, linkValue.length);
          }
          currentEditor.format('link', linkValue);

          //make sure component is aware about the change coming from the 'api' source
          onChange?.(currentEditor.getSemanticHTML());
        }
      }
    }
  }, [onChange]);

  const toolbarImageHandler = useCallback(() => {
    imageInputRef.current?.click();
  }, []);

  const onImagesChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        for (let i = 0, total = event.target.files.length; i < total; i++) {
          const file = event.target.files[i];
          const reader = new FileReader();
          reader.readAsDataURL(file);

          reader.onload = (event) => {
            if (event && event.target && event.target.result) {
              const imageUrl = event.target.result; // Base64-encoded image data

              // insert image into the editor
              if (editorRef.current) {
                const range = editorRef.current.getSelection();
                if (range) {
                  // insert new content
                  editorRef.current.insertEmbed(range.index, 'image', imageUrl);

                  // move caret at the end of inserted content
                  const length = editorRef.current.getLength();
                  editorRef.current.setSelection(length - 1);

                  //make sure component is aware about the change coming from the 'api' source
                  onChange?.(editorRef.current.getSemanticHTML());
                }
              }
            }
          };
        }
      }
    },
    [onChange]
  );

  const onValueChange = useCallback(
    debounce((_delta: Delta, _oldDelta: Delta, source: EmitterSource) => {
      if (isEditor === true && source === 'user') {
        // Use the semantic HTML value https://github.com/quilljs/quill/issues/3957#issuecomment-1945538748
        // Replace `&nbsp;` generated for ` ` by Quill https://github.com/slab/quill/issues/4509#issuecomment-2521334016
        const newValue = editorRef.current?.getSemanticHTML()?.replace(/((?:&nbsp;)*)&nbsp;/g, '$1 ');
        onChange?.(newValue);
      }
    }, 300),
    []
  );

  const textAreaClassNames = clsx('odin mt-1 [&_a]:text-url', {
    'text-readonly cursor-pointer': readOnly,
    'bg-mono-1': !readOnly && isEditor === true,
    'has-error': error, // marker class
    'is-readonly': readOnly || isEditor === false, // marker class
    // Clearing existing value from rich text area does not clear '<p></p>' from the field,
    hidden: (readOnly || isEditor === false) && (!value || value.length === 0 || value === EMPTY_VALUE),
  });

  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {(readOnly || isEditor === false) && (!value || value.length === 0 || value === EMPTY_VALUE) && (
        <EmptyFieldLabel />
      )}
      <div ref={containerRef} className={textAreaClassNames}>
        <div ref={editorContainerRef}></div>
        {/* React Quill Editor */}
        {isEditor === true && (
          <input
            id={guid}
            className="hidden"
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onImagesChanged}
            onClick={(event) => (event.currentTarget.value = '')}
          />
        )}
      </div>

      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
};

export default RichTextArea;
